/**
 * SIGNED IN, WITH NO HOME LINKED. Built from
 * `faithfully-kept-business/design/home/NoMembership.dc.html`, its structure and its words, with
 * the signed-in address in place of the sample one, kept whole with its full stop: it moves to
 * the next line as one piece rather than break at a hyphen of its own, and breaks anywhere only
 * where it is longer than the line.
 *
 * It says nothing about any household, because a sign-in proves an email address or a mobile number
 * and nothing else (CLAUDE.md binding rule 3): a home is linked only by an invitation ops sends. The
 * way forward is the phone, the email, and signing out to try the address or number the business
 * has on file. A sign-out that does not go through leaves her here, with the focus on Sign out and a
 * line under it that says so (`components/SignOut.tsx`).
 *
 * DRAWN IN PLACE OF A SIGNED-IN SCREEN, with no move between places: a place read again answering
 * 404, which is how a membership ops revokes in the middle of a session reads, or /api/me asked
 * again saying no home is linked. The page is then where she was, so the focus goes to its heading
 * as after a move (`components/RouteFocus.tsx`), as the door's does. On a first load it stays where
 * a page starts.
 *
 * SIGNED IN BY A MOBILE NUMBER (home #11), where the host can text, the page names the number, as
 * she reads one, where it names the address, and its words say "number" where they say "email",
 * word for word otherwise.
 *
 * AN INVITATION THAT WAITS FOR HER CODE (found on staging, 2026-11-05; `docs/seam.md` P118). A
 * session she already holds, whose households ops has all ended, and to whose address or number ops
 * has since sent an invitation, lands here: no code has opened it, so no home is linked. So while
 * /api/me says one waits, the page says so above its content, with the fresh code that opens it
 * (`components/InvitationWaits.tsx`), rather than leave her where nothing can be done but call; once
 * the code works, /api/me is asked again, and finds a home linked, and Home is drawn in this page's
 * place (`Entry.tsx`).
 */
import type { SignedInWith } from '../api.ts';
import { GHOST_BUTTON, INLINE_LINK, NAVY_BUTTON } from '../components/buttons.ts';
import { COLUMN } from '../components/column.ts';
import { Header } from '../components/Header.tsx';
import { InvitationWaits } from '../components/InvitationWaits.tsx';
import { useHeadingFocus } from '../components/RouteFocus.tsx';
import { SignOutFailed, useSignOut } from '../components/SignOut.tsx';
import { EMAIL_HREF, HOURS, PHONE_HREF, PHONE_SHOWN } from '../contact.ts';
import { signedInShown } from '../lib/identifier.ts';

/**
 * The words that name what she signed in with: the artboard's, and where she signed in by her
 * mobile number, the same words naming a number.
 */
const NAMED = {
  email: {
    heading: 'No home is linked to this email yet',
    different: 'Signed in with a different email from the one we have?',
  },
  text: {
    heading: 'No home is linked to this number yet',
    different: 'Signed in with a different number from the one we have?',
  },
} as const;

interface NoMembershipProps {
  /** The address or mobile number she signed in with. */
  readonly signedInWith: SignedInWith;
  readonly onSignedOut: () => void;
  /** Whether the page is drawn in place of a signed-in screen she was on, with no move. */
  readonly inPlace?: boolean;
  /** How many invitations wait for her code, as /api/me says it: the notice above, while any do. */
  readonly invitationsWaiting?: number;
  /** The code opened them: asks /api/me again, which draws Home here once a home is linked. */
  readonly onInvitationsOpened?: () => void;
}

export function NoMembership({
  signedInWith,
  onSignedOut,
  inPlace = false,
  invitationsWaiting = 0,
  onInvitationsOpened = () => undefined,
}: NoMembershipProps) {
  useHeadingFocus(inPlace);
  const leaving = useSignOut(onSignedOut);
  const named = NAMED[signedInWith.channel];

  return (
    <div className="flex min-h-dvh flex-col bg-surface-page">
      <Header />

      <main className={`${COLUMN} flex-1 px-5 py-8`}>
        <InvitationWaits
          signedInWith={signedInWith}
          waiting={invitationsWaiting}
          onOpened={onInvitationsOpened}
          className="mb-8"
        />
        <p className="font-mono text-small tracking-widest text-text-emphasis uppercase">
          Signed in
        </p>
        <h1 className="mt-2 font-serif text-h2 font-semibold text-pretty text-text-body">
          {named.heading}
        </h1>
        <p className="mt-4 text-body">
          You are signed in as{' '}
          <span className="inline-block max-w-full wrap-anywhere">{`${signedInShown(signedInWith)}.`}</span>
        </p>
        <p className="mt-3 text-body text-pretty">
          A home is linked to you only when we invite you to it, so nobody reaches a home by typing
          an address. If you expected to see yours, call us.
        </p>

        <div className="mt-7 flex flex-col gap-3">
          <a href={PHONE_HREF} className={NAVY_BUTTON}>
            {`Call ${PHONE_SHOWN}`}
          </a>
          <a href={EMAIL_HREF} className={GHOST_BUTTON}>
            Email us
          </a>
        </div>
        <p className="mt-4 text-small text-text-muted">{HOURS}</p>

        <div className="mt-8 border-t border-border-subtle pt-5">
          <p className="text-small text-text-muted">
            {named.different}{' '}
            <button type="button" onClick={leaving.signOut} className={INLINE_LINK}>
              Sign out
            </button>{' '}
            and use that one.
          </p>
          <SignOutFailed failed={leaving.failed} />
        </div>
      </main>
    </div>
  );
}
